"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";

// ── localStorage helpers ──────────────────────────────────────────────

const LS_CLIENT_ID = "bc.selectedClientId";
const LS_RECENT_IDS = "bc.recentClientIds";
const LS_PINNED_IDS = "bc.pinnedClientIds";
const MAX_RECENT = 5;

function readLocalStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeLocalStorage<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota errors — silently ignore
  }
}

// ── Context value type ────────────────────────────────────────────────

interface ClientContextValue {
  clientId: Id<"clients"> | undefined;
  setClientId: (id: Id<"clients"> | undefined) => void;
  clients: Doc<"clients">[];
  currentClient: Doc<"clients"> | undefined;
  isClientUser: boolean;
  isAgencyView: boolean;
  loading: boolean;
  recentClientIds: Id<"clients">[];
  pinnedClientIds: Id<"clients">[];
  togglePinned: (id: Id<"clients">) => void;
  brandLogoUrl: string | null;
  setBrandLogoUrl: (url: string | null) => void;
}

const ClientContext = createContext<ClientContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────

export function ClientProvider({ children }: { children: ReactNode }) {
  // Convex queries
  const clients = useQuery(api.clients.list);
  const clientAuthContext = useQuery(api.clientAuth.getMyClientContext);

  // Derived: is this a client (non-agency) user?
  const isClientUser =
    clientAuthContext !== undefined && clientAuthContext !== null;
  const clientUserClientId = clientAuthContext?.clientOrg?.clientId as
    | Id<"clients">
    | undefined;

  // Local state backed by localStorage
  const [selectedId, setSelectedId] = useState<Id<"clients"> | undefined>(() =>
    readLocalStorage<Id<"clients"> | undefined>(LS_CLIENT_ID, undefined),
  );
  const [recentClientIds, setRecentClientIds] = useState<Id<"clients">[]>(() =>
    readLocalStorage<Id<"clients">[]>(LS_RECENT_IDS, []),
  );
  const [pinnedClientIds, setPinnedClientIds] = useState<Id<"clients">[]>(() =>
    readLocalStorage<Id<"clients">[]>(LS_PINNED_IDS, []),
  );

  // Brand logo URL — set by ClientBrandApplier when a client brand kit is active
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);

  // Resolve the effective clientId
  const clientId = useMemo<Id<"clients"> | undefined>(() => {
    if (isClientUser && clientUserClientId) return clientUserClientId;
    return selectedId; // undefined = Agency View
  }, [isClientUser, clientUserClientId, selectedId]);

  // Setter — updates localStorage + pushes to recent
  const setClientId = useCallback(
    (id: Id<"clients"> | undefined) => {
      if (isClientUser) return; // client users can't switch
      setSelectedId(id);
      writeLocalStorage(LS_CLIENT_ID, id ?? null);

      if (id !== undefined) {
        setRecentClientIds((prev) => {
          const deduped = prev.filter((rid) => rid !== id);
          const next = [id, ...deduped].slice(0, MAX_RECENT);
          writeLocalStorage(LS_RECENT_IDS, next);
          return next;
        });
      }
    },
    [isClientUser],
  );

  // Pinned toggle
  const togglePinned = useCallback((id: Id<"clients">) => {
    setPinnedClientIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((pid) => pid !== id)
        : [...prev, id];
      writeLocalStorage(LS_PINNED_IDS, next);
      return next;
    });
  }, []);

  // Hydrate: if the stored selectedId isn't in the clients list, clear it
  useEffect(() => {
    if (clients && selectedId) {
      const exists = clients.some((c) => c._id === selectedId);
      if (!exists) {
        setSelectedId(undefined);
        writeLocalStorage(LS_CLIENT_ID, null);
      }
    }
  }, [clients, selectedId]);

  const currentClient = useMemo(
    () => clients?.find((c) => c._id === clientId),
    [clients, clientId],
  );

  const loading = clients === undefined || clientAuthContext === undefined;

  const value = useMemo<ClientContextValue>(
    () => ({
      clientId,
      setClientId,
      clients: clients ?? [],
      currentClient,
      isClientUser,
      isAgencyView: !isClientUser && clientId === undefined,
      loading,
      recentClientIds,
      pinnedClientIds,
      togglePinned,
      brandLogoUrl,
      setBrandLogoUrl,
    }),
    [
      clientId,
      setClientId,
      clients,
      currentClient,
      isClientUser,
      loading,
      recentClientIds,
      pinnedClientIds,
      togglePinned,
      brandLogoUrl,
    ],
  );

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useClientContext(): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (!ctx) {
    throw new Error("useClientContext must be used within a <ClientProvider>");
  }
  return ctx;
}
